import { BaseMessage } from "@app/websocket/websocket.service";
import { Point, Rect, Size } from "@shared/euclid";

export class ReviewMember {
    UserId: string;
    UserName: string;
    UserEmail: string;
    IsSyncing: boolean;
    Color: string;
    Size: number;
}

export enum Slot {
    Primary,
    Compare,
}

export class ReviewState {
    Asset: string;
    Members: ReviewMember[];
    Presenter: string;
    Comments: string[];
    Frame: number;
    IsPlaying: boolean;
    IsLooping: boolean;
    Transform: number[];
    PlayRange: number[];

    StrokeHistory: any;
    HistoryPosition: number;

    // Compare (see docs/design/review-compare-assets.md). CompareAsset === InvalidGuid means not comparing.
    CompareAsset: string = InvalidGuid;
    AssetType: number;
    CompareAssetType: number;
    ActiveSlot: Slot = Slot.Primary;
    CompareLocked: boolean;
    CompareFrame: number;
    CompareIsPlaying: boolean;
    ComparePlayRange: number[];
    CompareTransform: number[];

    isActive() {
        return this.Members.some((i) => i.IsSyncing);
    }

    isComparing(): boolean {
        return Boolean(this.CompareAsset) && this.CompareAsset !== InvalidGuid;
    }

    // The field name to use when sending a ReviewState update for whichever slot is currently active -
    // e.g. 'Frame' while Primary is active, 'CompareFrame' while Compare is active. Only the per-slot
    // fields listed in docs/design/review-compare-assets.md §3.2 are ever renamed; anything else (e.g.
    // SilentChange, IsLooping, RequestPresenter) is session-global and passes through unchanged.
    activeFieldName(baseName: string): string {
        const perSlotFields = ['Frame', 'IsPlaying', 'PlayRange', 'Transform'];
        if (this.ActiveSlot === Slot.Compare && perSlotFields.includes(baseName)) {
            return `Compare${baseName}`;
        }
        return baseName;
    }
}

export enum SyncState {
    None,
    Syncing,
}

export enum PresentingState {
    None,
    Self,
    Other,
}

export enum AnnotationTool {
    None,
    Cursor,
    Pencil,
    Eraser
}

export class AssetBounds {
    bounds: Rect;
    relativeBounds: Rect;
    scale: number;
    assetSize: Size;
    assetId: string;
    slot: Slot = Slot.Primary;
}

export const InvalidGuid = '00000000-0000-0000-0000-000000000000';

// Which compare slot (if any) an Asset row is flagged for - 'A' is Primary, 'B' is Compare, matching the
// badge letters in the asset list and viewport pane headers (docs/spec/compare-ui-spec.md).
export type CompareRole = 'A' | 'B' | null;

export enum CursorAction {
    Start,
    Push,
    End,
    Clear,
}

export class UserCursor extends BaseMessage {
    Id: string;
    Action: CursorAction;
    Point: number[];
    Points: number[][];
    UserId: string;
    pos: Point;
    Color: string;
    Size: number;
    Time: number;
    Frame: number;
    Tool: AnnotationTool;
}

export enum HistoryAction {
    Clear,
    Undo,
    Redo
}

export class StrokeHistory extends BaseMessage {
    Action: HistoryAction;
    ReviewId: string;
    UserId: string;
    HistoryPointer: number;
}

export class DeepLink {
    public asset: string;
    public frame: number;

    public get isConsumed() {
        return this.asset === null;
    }

    public consume(): void {
        this.asset = null;
    }
}
